import { ChangeDetectionStrategy, Component, HostBinding, inject, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import {
  Capability,
  PolicyFindingFilterField,
  ExprOp,
  GetPolicyFindingsGroupedQuery,
  GetPolicyFindingsGroupedQueryVariables,
  GetPolicyFindingsQuery,
  GetPolicyFindingsQueryVariables,
  PolicyFindingFragment,
  PolicyFindingsExpressionsParams,
} from '@uigy/generated/graphql';
import { IconsEnum } from '@uigy/generated/icons.enum';
import { EventsModule } from '@uigy/modules/events/events.module';
import {
  PolicyFindingsActionsService,
} from '@uigy/modules/findings/components/findings-policy-violations/services/policy-findings-actions.service';
import {
  PolicyFindingsGridSettingsService,
} from '@uigy/modules/findings/components/findings-policy-violations/services/policy-findings-grid-settings.service';
import {
  PolicyFindingsService,
} from '@uigy/modules/findings/components/findings-policy-violations/services/policy-findings.service';
import {
  PolicyFindingsSideDrawerService,
} from '@uigy/modules/findings/components/findings-side-panel/services/policy-findings-side-drawer.service';
import { RoutesStatesNamesEnum } from '@uigy/shared/enums/routesStatesNames.enum';
import { MaterialModule } from '@uigy/shared/material-module/material.module';
import { LanguageService } from '@uigy/shared/services/language.service';
import { drawerDefaultWidth } from '@uigy/shared/components/ui-drawer/constants';
import { UiGridComponent } from '@uigy/shared/components/ui-grid/components/ui-grid/ui-grid.component';
import { UiGridIdsEnum } from '@uigy/shared/components/ui-grid/enums/ui-grid-ids.enum';
import { GridUtils } from '@uigy/shared/components/ui-grid/grid-utils';
import { CustomMenuItemParams } from '@uigy/shared/components/ui-grid/models/action-menu';
import { RowsSelection, UiGridOptions } from '@uigy/shared/components/ui-grid/models/ui-grid';
import { AppIdsEnum } from '@uigy/shared/directives/app-id/appIds.enum';
import { HOST_ATTR_APP_ID } from '@uigy/shared/directives/app-id/host-attributes.constant';
import { I18NextModule } from 'angular-i18next';
import { Subject } from 'rxjs';
import {
  SaveFiltersFieldNames,
} from '@uigy/shared/components/ui-grid/components/filters/saved-filters/save-filter-form/interfaces';
import { ActionButtonVariant } from '@uigy/shared/components/ui-buttons-action-bar/models';
import { CopyTextRendererComponent } from '@uigy/shared/modules/grid/cell-renderers/copy-text-renderer/copy-text-renderer.component';

@Component({
  selector: 'app-findings-policy-violations',
  imports: [
    UiGridComponent,
    MaterialModule,
    I18NextModule,
    EventsModule
  ],
  templateUrl: './findings-policy-violations.component.html',
  styleUrl: './findings-policy-violations.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FindingsPolicyViolationsComponent implements OnInit {
  private languageService = inject(LanguageService);
  private findingsGridSettingsService = inject(PolicyFindingsGridSettingsService);
  private findingsService = inject(PolicyFindingsService);
  private router = inject(Router);
  private actionsService = inject(PolicyFindingsActionsService);
  public findingsSideDrawerService = inject(PolicyFindingsSideDrawerService);

  private getAllFindings$ = (pagingParams: GetPolicyFindingsQueryVariables) => this.findingsService.getFindings(pagingParams);
  private getAllFindingsGrouping$ = (pagingParams: GetPolicyFindingsGroupedQueryVariables) => this.findingsService.getFindingsGrouped(pagingParams);

  public readonly findingsTexts = this.languageService.texts.findings;
  protected readonly IconsEnum = IconsEnum;

  @HostBinding(HOST_ATTR_APP_ID)
  public readonly hostAppId: string = AppIdsEnum.page_findings;

  public gridOptions?: UiGridOptions<PolicyFindingFragment, GetPolicyFindingsQuery, GetPolicyFindingsGroupedQuery>;
  private refetchData$ = new Subject<void>();

  ngOnInit(): void {
    this.gridOptions = {
      gridId: UiGridIdsEnum.POLICY_FINDINGS,
      gridTitle: this.findingsTexts.policy_violations.grid_title,
      exportFileName: this.findingsTexts.title,
      dataSource: this.getAllFindings$,
      groupingDataSource: this.getAllFindingsGrouping$,
      transformFilterModel: this.findingsGridSettingsService.findingsTransformFilterModel,
      columnDefs: this.findingsGridSettingsService.findingsColumnDefs,
      rowSelection: 'multiple',
      actionMenu: this.policyFindingsActionMenu(),
      refetchData: this.refetchData$,
      suppressDefaultSelection: true,
      activateUrlParams: true,
      headerOptions: {
        viewportOffset: drawerDefaultWidth(),
        globalButtons: [{
          button_variant: 'secondary',
          variant: ActionButtonVariant.regular,
          action: this.navigateToAllEvents,
          label: this.findingsTexts.policy_violations.events_link,
          rightIcon: IconsEnum.external_link
        }],
        renderSaveFilterButton: true,
        renderRestoreFiltersButton: true,
        savedFiltersFieldName: SaveFiltersFieldNames.PolicyFindingFilterField
      },
      noRowsOverlayComponentParams: {
        title: this.findingsTexts.policy_violations.no_results.title,
        items: [
          {
            text: this.findingsTexts.policy_violations.no_results.description,
            actionButton: {
              text: this.findingsTexts.policy_violations.no_results.button,
              buttonType: 'link',
              action: () => this.router.navigate([RoutesStatesNamesEnum.settings, RoutesStatesNamesEnum.settingsAssets, RoutesStatesNamesEnum.assetSettings]),
            },
          },
        ],
      },
      components: {
        copyTextRendererComponent: CopyTextRendererComponent,
      },
    };
  }

  public navigateToAllEvents(): void {
    window.open(`${RoutesStatesNamesEnum.events}/${RoutesStatesNamesEnum.allEvents}/${RoutesStatesNamesEnum.eventsDetailsTab}`, '_blank');
  }

  private policyFindingsActionMenu = (): ((rowsSelection: RowsSelection) => CustomMenuItemParams<PolicyFindingFragment>[]) => {
    return (rowsSelection: RowsSelection): CustomMenuItemParams<PolicyFindingFragment>[] => {
      if (rowsSelection.isInSelectAllMode || rowsSelection.selectedRows.length > 1) {
        return this.getBulkMenuItems(rowsSelection);
      }

      const policyFinding: PolicyFindingFragment | undefined =
        rowsSelection.selectedRows.length === 1
          ? rowsSelection.gridApi?.getRowNode(rowsSelection.selectedRows[0])?.data
          : rowsSelection.gridApi?.getSelectedRows()[0] ?? undefined;

      if (!policyFinding) {
        return [];
      }

      return this.actionsService.gridMenuItemsForFinding(policyFinding, this.refetchData$);
    };
  };

  private getBulkMenuItems(rowsSelection: RowsSelection): CustomMenuItemParams<PolicyFindingFragment>[] {
    const search = rowsSelection?.gridApi?.getQuickFilter();
    const totalCount = rowsSelection?.totalCount ?? 0;

    let filter: PolicyFindingsExpressionsParams = {
      field: PolicyFindingFilterField.Id,
      values: rowsSelection.selectedRows,
      op: rowsSelection?.isInSelectAllMode ? ExprOp.NotIn : ExprOp.In,
    };

    if (rowsSelection?.gridApi?.getFilterModel()) {
      filter = GridUtils.combineFilters(filter, GridUtils.convertFilters<PolicyFindingFilterField>(rowsSelection?.gridApi?.getFilterModel(), rowsSelection?.gridApi) ?? undefined);
    }

    return [
      {
        label: this.findingsTexts.policy_violations.grid_actions.resolve.button,
        onClick: () => this.actionsService.openResolveSlider(filter, search, true, this.refetchData$, totalCount),
        capability: Capability.WriteEvents,
        id: AppIdsEnum.findings_action_resolve
      }
    ];
  }

  public focusedRowChanged(id: string|undefined): void {
      this.findingsSideDrawerService.setSelectedFinding(id);
  }

}
